import logger from '@scale/frontend-lib/logging';
import apiFetch from '@scale/frontend-lib/api-fetch';
import API from '@scale/frontend-lib/dashboard-api';
import { UserIdentityRO } from '@scale/identity-service-fetch-client';
import { AuditStatusSource, ProjectDisableReason } from '@scale/shared-types';
import { QualityProfileAuditStatus, ReviewLevel } from '@scale/shared/defaults';
import { WorkerTeamMembers } from '@scale/shared/types/api/corp-api/workerTeams';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query-v4';
import _ from 'lodash';
import { qmQueryClient } from 'react-scale/corp/lib/api/queryClient';
import { toast } from 'react-toastify';
import {
  GetGivenQualityScoreByReviewLevelAggregatedResponse,
  WorkerProjectBmStatsResponse,
  ReviewerHubDataResult,
  TaskAttemptInfoByProjectAndDateRangeAggregated,
} from '@scale/shared-types/src/genai-quality/routes';
import { CardType } from '@scale/genai-compass-shared/cardTypes';
import type { TaskerQualityDataAdapterContext } from '@scale/genai-compass-shared/cards/tasker_quality_data_adapter';
import { CommunicationService } from '@scale/compass-ui/communicationService';
import { unflattenData } from '@scale/shared/types/compass/cards/adapter/taskerqualitydata';
import {
  GetFlaggedReviewersRequest,
  GetFlaggedReviewersResponse,
  InvalidateFlaggedReviewsRequest,
  InvalidateFlaggedReviewsResponse,
} from '@scale/shared/types/chat';
import { ContributorsQualityScores } from '@scale/shared/types/quality';

const MAIN_QUERY_KEY = 'worker-performance-page';

const DEFAULT_AUDITED_COUNT_THRESHOLD = 10;

const DEFAULT_AVERAGE_QUALITY_THRESHOLD = 3.5;

export const DEFAULT_MAX_AUDITS_THRESHOLD = 10;

export type QualityProfileConfig = {
  auditedCountThreshold: number;
  averageQualityThreshold: number;
  manualUpdatesByTasker?: {
    [key: string]: {
      auditStatus: QualityProfileAuditStatus;
    };
  };
  automaticAuditStatusSource?: AuditStatusSource[];
  attemptsToAuditThreshold?: number;
};

export type QualityProfileFilter = {
  minScoredTaskAttempts: number;
  minQualityScore: number;
  maxQualityScore: number;
  minBenchmarkAverage: number;
  maxBenchmarkAverage: number;
  minSBQPercentage: number;
  maxSBQPercentage: number;
  minPDR: number;
  maxPDR: number;
  showOnlyCurrentReviewLevel: boolean;
  hideDisabledContributors: boolean;
  workerSkillsOrSpecializations: string;
  showOnlyMarketplaceContributors: boolean;
  showOnlyDirectAssignedContributors: boolean;
  showOnlyOracleContributors: boolean;
  showOnlyReviewerCandidates: boolean;
};

interface ReviewerHubCompassResult {
  result: ReviewerHubDataResult;
  asOfEpochMillis: number;
}

export type TaskAttemptInfo = {
  _id?: string;
  attemptedBy?: string;
  attemptedAt?: string;
  attemptedAtReviewLevel?: number;
  v2TimeSpentSecs?: number;
  v2ActiveTimeSpentSecs?: number;
  payoutAmountHundredthsOfCents?: number;
};

export type ChartSeries = {
  id: string | number;
  data: Array<{
    x: number | string | Date;
    y: number | string | Date;
  }>;
  color?: string;
};

export type LineChartDataType =
  | ChartSeries[]
  | {
      totalTime: Record<string, ChartSeries>;
      activeTime: Record<string, ChartSeries>;
    };

export function useGetProjectWorkerLastAttempts(projectId: string) {
  return useQuery<WorkerTeamMembers[]>(
    [MAIN_QUERY_KEY, 'project-worker-last-attempts', projectId],
    async () =>
      apiFetch<{ result: any[] }>(
        null,
        `internal/self_serve/qualityProfile/getProjectWorkerLastAttempts`,
        {
          method: 'GET',
          query: {
            projectId,
          },
        },
      ).then(res => res.result),
    {
      enabled: !!projectId,
    },
  );
}

export function useGetReviewerHubDataByUserAndLevel({
  projectId,
  dateRange,
  refreshTokenRef,
}: {
  projectId: string;
  dateRange: Date[];
  refreshTokenRef: React.RefObject<number>;
}) {
  return useQuery<ReviewerHubCompassResult>(
    [MAIN_QUERY_KEY, 'reviewer-hub-worker-data-by-user-and-level', projectId, dateRange],
    async () => {
      const refreshData = !!refreshTokenRef.current && refreshTokenRef.current > 0;

      const cardContext: TaskerQualityDataAdapterContext = {
        cardType: CardType.TASKER_QUALITY_DATA_ADAPTER,
        projectId,
        dateRange: {
          startDate: dateRange[0],
          endDate: dateRange[1],
        },
      };

      const { data, summary } = await CommunicationService.computeContext({
        cards: [cardContext],
        externalContext: {
          refreshData,
        },
        sendAllData: true,
      });

      // Generalized compute context data type is Record<string, any>[], the specific type for reviewer hub is TaskAttemptInfoByProjectAndDateRangeAggregated[]
      // See src/types/compass/cards/adapter/taskerqualitydata.ts
      return {
        result: unflattenData(data as TaskAttemptInfoByProjectAndDateRangeAggregated[]),
        asOfEpochMillis: summary.asOfEpochMillis,
      };
    },
    {
      keepPreviousData: true,
      staleTime: Infinity,
    },
  );
}

export function useGetAuditsForUserAndProject({
  userId,
  projectId,
  dateRange,
}: {
  userId: string | null;
  projectId: string | null;
  dateRange: Date[];
}) {
  return useQuery(
    [MAIN_QUERY_KEY, 'audits-for-user-and-project', userId, projectId, dateRange],
    async () => {
      return await apiFetch(null, `internal/self_serve/qualityProfile/getAuditsForUserAndProject`, {
        method: 'GET',
        query: {
          userId,
          projectId,
          dateRange,
        },
      });
    },
    {
      enabled: !!userId && !!projectId,
    },
  );
}

export function useGetPermissioningHistory({
  userId,
  projectId,
}: {
  userId: string;
  projectId: string;
}) {
  return useQuery(['permissioning-history', userId, projectId], async () => {
    const query = `corp-api/lookup/${userId}/${projectId}/permissioningHistory`;

    return await apiFetch(null, query, {
      method: 'GET',
    });
  });
}

export function useGivenQualityScoreByUserAndReviewLevel({
  projectId,
  dateRange,
}: {
  projectId: string;
  dateRange: Date[];
}) {
  return useQuery<GetGivenQualityScoreByReviewLevelAggregatedResponse['result']>(
    [MAIN_QUERY_KEY, 'project-given-quality-score-by-user-and-review-level', projectId, dateRange],
    async () => {
      const { result: givenQualityScoresByUserAndReviewLevel } =
        await apiFetch<GetGivenQualityScoreByReviewLevelAggregatedResponse>(
          null,
          'internal/self_serve/qualityProfile/getGivenQualityScoreByUserAndReviewLevel',
          {
            method: 'GET',
            query: {
              projectId,
              startDate: dateRange[0],
              endDate: dateRange[1],
            },
          },
        );

      return givenQualityScoresByUserAndReviewLevel;
    },
  );
}

export function useGetBasicWorkerInfo({ userId }: { userId: string | null }) {
  return useQuery(
    ['basic-worker-info', userId],
    async () => {
      const query = `corp-api/lookup/${userId}`;

      const userObject = await apiFetch(null, query, {
        method: 'GET',
      });

      return userObject;
    },
    {
      enabled: !!userId,
    },
  );
}

export function useGetQualityProfileConfig({ projectId }: { projectId: string }) {
  return useQuery<QualityProfileConfig>([MAIN_QUERY_KEY, 'project-config', projectId], () =>
    API.selfserve
      .getQualityProfileConfig({
        projectId,
      })
      .then(res => {
        return {
          auditedCountThreshold: DEFAULT_AUDITED_COUNT_THRESHOLD,
          averageQualityThreshold: DEFAULT_AVERAGE_QUALITY_THRESHOLD,
          automaticAuditStatusSource: [AuditStatusSource.GQA, AuditStatusSource.SpeedAudit],
          attemptsToAuditThreshold: DEFAULT_MAX_AUDITS_THRESHOLD,
          ...res.result,
        };
      }),
  );
}

export function useUpdateQualityProfileConfig() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      projectId,
      qualityProfileConfig,
    }: {
      projectId: string;
      qualityProfileConfig: QualityProfileConfig;
    }) =>
      API.selfserve.updateQualityProfileConfig({
        projectId,
        qualityProfileConfig,
      }),
    {
      onSuccess: (_, { projectId }: { projectId: string }) => {
        queryClient.invalidateQueries({
          queryKey: [MAIN_QUERY_KEY, 'project-config', projectId],
        });
        queryClient.invalidateQueries({
          queryKey: ['projectDataProviderQuery', projectId, 'projectAuditStatusByWorker'],
        });
      },
    },
  );
}

export function useGetUsers({ userIds }: { userIds?: string[] }) {
  return useQuery<UserIdentityRO[]>(
    [MAIN_QUERY_KEY, 'users', userIds],
    () => API.customer.getUserIdentities({ userIds: userIds! }).then(res => res.users),
    { enabled: !!userIds?.length },
  );
}

export function useGetPendingV3HumanNodesForProject({ projectId }: { projectId: string }) {
  return useQuery([MAIN_QUERY_KEY, 'users-permission-group-task-permissions', projectId], () =>
    apiFetch<{ result: any[] }>(
      null,
      `internal/self_serve/qualityProfile/getPendingV3HumanNodesForProject`,
      {
        method: 'GET',
        query: {
          projectId,
        },
      },
    ).then(res => res.result),
  );
}

export function useGetWorkerTagMappings({
  workerIds,
  tags,
}: {
  workerIds: string[];
  tags: string[];
}) {
  return useQuery(
    [MAIN_QUERY_KEY, 'worker-tag-mappings', workerIds, tags],
    () => API.selfserve.getWorkerTagMappings({ workerIds, tags }).then(res => res.result),
    { enabled: !!workerIds.length },
  );
}

export function useGetWorkerSkillsAndSpecializations({
  projectId,
  workerIds,
}: {
  projectId: string;
  workerIds: string[];
}) {
  return useQuery(['workerSkillsAndSpecializations', projectId, workerIds], () =>
    getWorkerSkillsAndSpecializationsByBatch({
      projectId,
      workerIdsChunked: _.chunk(workerIds, 200),
    }),
  );
}

async function getWorkerSkillsAndSpecializationsByBatch({
  projectId,
  workerIdsChunked,
}: {
  projectId: string;
  workerIdsChunked: string[][];
}) {
  if (workerIdsChunked.length === 0) {
    return { data: [], isSuccess: false };
  }
  try {
    const results = (await Promise.allSettled(
      workerIdsChunked.map(async workerIds => {
        if (workerIds.length > 0) {
          return await apiFetch(
            null,
            `internal/self_serve/qualityProfile/getWorkerSkillsAndSpecializations`,
            {
              method: 'POST',
              body: {
                projectId,
                workerIds,
              },
            },
          );
        }
      }),
    )) as { status: 'fulfilled' | 'rejected'; value: any }[];

    const data = results
      .filter(result => result.status === 'fulfilled')
      .map(result => result?.value?.result);
    const combinedData = data.reduce((accumulator, currentData) => {
      return { ...accumulator, ...currentData };
    }, {});

    const isSuccess = results.every(result => result.status === 'fulfilled');
    return { data: combinedData, isSuccess };
  } catch (err) {
    logger.error('Failed to fetch worker skills and specializations in project', { projectId });
    return { data: [], isSuccess: false };
  }
}

export function useGetBenchmarkStats({
  projectId,
  startDate,
  endDate,
  workerIds,
  enabled,
}: {
  projectId: string;
  startDate: Date;
  endDate: Date;
  workerIds: string[];
  enabled: boolean;
}) {
  return useQuery<WorkerProjectBmStatsResponse>(
    [MAIN_QUERY_KEY, 'bm-stats', projectId, startDate, endDate, workerIds],
    () =>
      API.selfserve
        .getBenchmarkStats({
          projectId,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          workerIds: workerIds!,
        })
        .then(res => res.result),
    { enabled: enabled && workerIds.length > 0 },
  );
}

export function useGetWorkerBMStats({
  projectId,
  minDate,
  workerId,
  includeDisabledBms,
  includeSkippedAttempts,
}: {
  projectId: string;
  minDate: Date;
  workerId: string;
  includeDisabledBms?: boolean;
  includeSkippedAttempts?: boolean;
}) {
  return useQuery(
    [MAIN_QUERY_KEY, 'worker-bm-stats', projectId, minDate, workerId, includeDisabledBms],
    () =>
      API.selfserve
        .getWorkerBenchmarkStats({
          projectId,
          minDate: minDate.toISOString(),
          workerId,
          includeDisabledBms: !!includeDisabledBms,
          includeSkippedAttempts: !!includeSkippedAttempts,
        })
        .then(res => res.result),
    { enabled: !!workerId },
  );
}

export function useGetWorkerAutoDisables({
  projectId,
  workerId,
}: {
  projectId: string;
  workerId: string;
}) {
  return useQuery(
    [MAIN_QUERY_KEY, 'worker-auto-disables', projectId, workerId],
    () =>
      API.selfserve
        .getWorkerAutoDisables({
          projectId,
          workerId,
        })
        .then(res => res.result),
    { enabled: !!workerId },
  );
}

export function useGetDisableAccuracyThresholds({ projectId }: { projectId: string }) {
  return useQuery(
    [MAIN_QUERY_KEY, 'disable-accuracy-thresholds', projectId],
    () =>
      API.selfserve.getDisableAccuracyThresholds({
        projectId,
      }),
    { enabled: !!projectId },
  );
}

export function useGetPendingSubtasksForProject({ projectId }: { projectId: string }) {
  return useQuery([MAIN_QUERY_KEY, 'subtasks', projectId], () =>
    apiFetch<{ result: any[] }>(
      null,
      `internal/self_serve/qualityProfile/getPendingSubtasksForProject`,
      {
        method: 'GET',
        query: {
          projectId,
        },
      },
    ).then(res => res.result),
  );
}

export function useGetTaskAttemptsForTasks({ taskIds }: { taskIds: string[] }) {
  return useQuery([MAIN_QUERY_KEY, 'recent-task-attempts-for-tasks', taskIds], () =>
    apiFetch(null, `internal/self_serve/qualityProfile/getTaskAttemptsForTasks`, {
      method: 'POST',
      body: {
        taskIds,
      },
    }).then(res => res.result),
  );
}

export function useUpdateReviewLevelTasker() {
  return useMutation(
    ({
      projectId,
      userIds,
      reviewLevel,
    }: {
      projectId: string;
      userIds: string[];
      reviewLevel?: ReviewLevel;
    }) =>
      API.corp.massAddPermissions({
        project: projectId,
        reviewMode: true,
        reviewLevel: reviewLevel ?? ReviewLevel.Level1,
        sampleRate: 100,
        userIds,
        needCourses: false,
        sendReceiptEmail: true,
        sendReceiptSlack: true,
      }),
    {
      onSuccess: () => {
        qmQueryClient.invalidateQueries([MAIN_QUERY_KEY], {
          exact: false,
        });
        toast.success('Successfully updated review level of contributors');
      },
    },
  );
}

export function useDisableTasker() {
  return useMutation(
    ({
      projectId,
      userIds,
      disableReason,
    }: {
      projectId: string;
      userIds: string[];
      disableReason: ProjectDisableReason;
    }) =>
      API.corp.disableTaskersOneProject({
        projectId,
        idsToDisable: userIds,
        rejectPendingAttempts: false,
        sendEmails: false,
        disableReason,
        sendReceiptEmail: true,
        sendReceiptSlack: true,
      }),
    {
      onSuccess: () => {
        qmQueryClient.invalidateQueries([MAIN_QUERY_KEY], {
          exact: false,
        });
        toast.success('Successfully disabled contributors');
      },
    },
  );
}

export function usePurgeTasker() {
  return useMutation(
    ({
      projectId,
      userIds,
      shouldSbq,
    }: {
      projectId: string;
      userIds: string[];
      shouldSbq: boolean;
    }) =>
      API.corp.purgeTaskersWorkOneProject({
        projectId,
        workerIds: userIds,
        shouldSbq,
      }),
    {
      onSuccess: () => {
        toast.success('Successfully purged contributors');
      },
    },
  );
}

export function useGetFlaggedReviewers({ projectId }: GetFlaggedReviewersRequest) {
  return useQuery([MAIN_QUERY_KEY, 'flagged-reviewers', projectId], () =>
    apiFetch<GetFlaggedReviewersResponse>(
      null,
      `internal/self_serve/qualityProfile/getFlaggedReviewers`,
      {
        method: 'GET',
        query: { projectId },
      },
    ),
  );
}
export function useInvalidateFlaggedReviews({
  projectId,
  refetchData,
}: {
  projectId: string;
  refetchData: () => void;
}) {
  return useMutation(
    ({ attemptIds, decision, suggestedScore }: InvalidateFlaggedReviewsRequest) =>
      apiFetch<InvalidateFlaggedReviewsResponse>(
        null,
        `internal/self_serve/qualityProfile/invalidateFlaggedReviews`,
        {
          method: 'POST',
          body: {
            attemptIds,
            decision,
            suggestedScore,
          },
        },
      ),
    {
      onSuccess: () => {
        qmQueryClient.invalidateQueries([MAIN_QUERY_KEY, 'flagged-reviewers', projectId], {
          exact: false,
        });
        toast(`Successfully updated dispute request`, {
          position: 'bottom-center',
          autoClose: 1000,
          progressStyle: { background: 'green' },
        });
        refetchData();
      },
    },
  );
}

export function useGetContributorsQualityScore({
  projectId,
  dateRange,
}: {
  projectId: string;
  dateRange: Date[];
}) {
  return useQuery([MAIN_QUERY_KEY, 'contributors-quality-score', projectId, dateRange], () => {
    return apiFetch<ContributorsQualityScores>(
      null,
      `internal/self_serve/qualityProfile/getContributorsQualityScore`,
      {
        method: 'GET',
        query: {
          projectId,
          startDate: dateRange[0],
          endDate: dateRange[1],
        },
      },
    );
  });
}
